"use client";

import { Button, Empty, Flex, Skeleton, Typography } from "antd/lib";
import FadeIn from "@/src/components/Common/FadeIn/FadeIn";
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { CheckingParticipantsEligibilityResponse, Event, EventParticipantsResponse } from "@/types/events";
import { useTranslation } from "react-i18next";
import { useEventsFetchers } from "@/hooks/useEventsFetchers";
import EventParticipantsTable from "@/src/components/Events/EventParticipantsTable";
import dynamic from "next/dynamic";
import { lazyRetry } from "@/utils/helpers";
import { useAuth0 } from "@auth0/auth0-react";
import { App } from "antd";
import { EventsActivityTypes, EventsEquippingLevels } from "@/constants/events";

const AddingParticipantsToEventByEmailsModal = dynamic(
  () => lazyRetry(() => import("@/src/components/Events/modals/AddingParticipantsToEventByEmailsModal")),
  {
    ssr: false,
  },
);
const AddingParticipantsToEventByFullNamesModal = dynamic(
  () => lazyRetry(() => import("@/src/components/Events/modals/AddingParticipantsToEventByFullNamesModal")),
  {
    ssr: false,
  },
);
const CheckingParticipantsEligibilityResultModal = dynamic(
  () => lazyRetry(() => import("@/src/components/Events/modals/CheckingParticipantsEligibilityResultModal")),
  {
    ssr: false,
  },
);
const FixEventModal = dynamic(() => lazyRetry(() => import("@/src/components/Events/modals/FixEventModal")), {
  ssr: false,
});

const { Title } = Typography;

enum AddingParticipantsTypes {
  emails = "emails",
  fullNames = "fullNames",
}

interface EventParticipantsProps {
  materialsRequired?: boolean;
  ageRequired?: boolean;
  mainFacilitatorId?: string;
  isInReportingContext?: boolean;
  withActions?: boolean;
  eventId: string;
  eventType: EventsActivityTypes;
  equippingLevel: EventsEquippingLevels | null;
  participantsAreLoading: boolean;
  participants?: EventParticipantsResponse;
  setParticipants: Dispatch<SetStateAction<EventParticipantsResponse | undefined>>;
  setParticipantsAreLoading: Dispatch<SetStateAction<boolean>>;
  setParticipantsChangingIsLoading?: Dispatch<SetStateAction<boolean>>;
  participantsRef?: React.MutableRefObject<any>;
  setParticipantsAreRendered?: Dispatch<SetStateAction<boolean>>;
  isEquippingReportEventWithConfidentialParticipants?: Event["has_confidential_participants"];
}

const EventParticipants: React.FC<EventParticipantsProps> = ({
  mainFacilitatorId,
  materialsRequired = false,
  ageRequired = false,
  isInReportingContext = false,
  withActions = true,
  eventId,
  eventType,
  equippingLevel,
  participantsAreLoading,
  participants,
  setParticipants,
  setParticipantsAreLoading,
  setParticipantsChangingIsLoading,
  participantsRef,
  setParticipantsAreRendered,
  isEquippingReportEventWithConfidentialParticipants = 0,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth0();
  const { message } = App.useApp();

  if (!user) {
    const error = t("common.messages.unavailablePage");
    message.error(error);
    throw new Error(error);
  }

  const { fetchEventParticipants } = useEventsFetchers();

  const [checkingParticipantsEligibilityResult, setCheckingParticipantsEligibilityResult] =
    useState<CheckingParticipantsEligibilityResponse>();
  const [isAddingParticipantsByEmailsModalOpen, setIsAddingParticipantsByEmailsModalOpen] = useState(false);
  const [isAddingParticipantsByFullNamesModalOpen, setIsAddingParticipantsByFullNamesModalOpen] = useState(false);
  const [isCheckingParticipantsEligibilityResultModalOpen, setIsCheckingParticipantsEligibilityResultModalOpen] =
    useState(false);
  const [isFixEventModalOpen, setIsFixEventModalOpen] = useState(false);
  const [eventMaterialRequired, setEventMaterialRequired] = useState(materialsRequired);
  const [eventAgeRequired, setEventAgeRequired] = useState(ageRequired);
  const [addingParticipantsType, setAddingParticipantsType] = useState<AddingParticipantsTypes>();

  const participantsEmails = useMemo(() => {
    if (!participants) return [];

    return Object.values(participants).map((participant) => participant.email);
  }, [Object.entries(participants || {}).length]);

  useEffect(() => {
    if (!participantsAreLoading) return;

    const fetchParticipants = async () => {
      const participantsResponse = await fetchEventParticipants(eventId, setParticipantsAreLoading);

      setParticipants(participantsResponse);
      setParticipantsAreLoading(false);
    };

    fetchParticipants();
  }, [Object.entries(participants || {}).length, isEquippingReportEventWithConfidentialParticipants]);

  useEffect(() => {
    if (!participantsAreLoading && setParticipantsAreRendered) setParticipantsAreRendered(true);
  }, [participantsAreLoading]);

  const addingParticipantsByEmailsHandler = () => {
    if (eventMaterialRequired || eventAgeRequired) {
      setIsFixEventModalOpen(true);
      setAddingParticipantsType(AddingParticipantsTypes.emails);
    } else {
      setIsAddingParticipantsByEmailsModalOpen(true);
    }
  };

  const addingParticipantsByFullNamesHandler = () => {
    if (eventMaterialRequired || eventAgeRequired) {
      setIsFixEventModalOpen(true);
      setAddingParticipantsType(AddingParticipantsTypes.fullNames);
    } else {
      setIsAddingParticipantsByFullNamesModalOpen(true);
    }
  };

  return (
    <Flex vertical gap={24} ref={participantsAreLoading ? null : participantsRef}>
      <Flex gap={16} justify="space-between" align="center">
        <Title level={4} style={{ marginBottom: 0, flexShrink: 0 }}>
          {isInReportingContext
            ? t("createReportPage.sections.participants.title")
            : t("eventDetailsPage.participants.title")}
        </Title>
        {withActions && (
          <Flex gap={16}>
            <Button onClick={addingParticipantsByEmailsHandler}>
              {user.app_coordinator &&
              eventType === EventsActivityTypes.equipping &&
              equippingLevel === EventsEquippingLevels.initial
                ? t("eventDetailsPage.participants.buttons.addByEmails")
                : t("common.actions.add")}
            </Button>
            {user.app_coordinator &&
              eventType === EventsActivityTypes.equipping &&
              equippingLevel === EventsEquippingLevels.initial && (
                <Button onClick={addingParticipantsByFullNamesHandler}>
                  {t("eventDetailsPage.participants.buttons.addByFullNames")}
                </Button>
              )}
          </Flex>
        )}
      </Flex>

      <Skeleton active loading={participantsAreLoading}>
        <FadeIn>
          {!!participants && Object.entries(participants).length ? (
            <EventParticipantsTable
              eventId={eventId}
              participants={participants}
              setDeleteParticipantIsLoading={setParticipantsChangingIsLoading}
              setParticipants={setParticipants}
              isEquippingReportEventWithConfidentialParticipants={isEquippingReportEventWithConfidentialParticipants}
              withActions={withActions}
              isInReportingContext={isInReportingContext}
            />
          ) : (
            <Empty />
          )}
        </FadeIn>
      </Skeleton>
      <AddingParticipantsToEventByEmailsModal
        eventId={eventId}
        isEquippingReportEventWithConfidentialParticipants={isEquippingReportEventWithConfidentialParticipants}
        isModalOpen={isAddingParticipantsByEmailsModalOpen}
        setIsModalOpen={setIsAddingParticipantsByEmailsModalOpen}
        setCheckingParticipantsEligibilityResult={setCheckingParticipantsEligibilityResult}
        setParticipants={setParticipants}
        participantsEmails={participantsEmails}
        setCheckingParticipantsEligibilityOrAddingIsLoading={setParticipantsChangingIsLoading}
        setIsCheckingParticipantsEligibilityResultModalIsOpen={setIsCheckingParticipantsEligibilityResultModalOpen}
      />
      <AddingParticipantsToEventByFullNamesModal
        eventId={eventId}
        isModalOpen={isAddingParticipantsByFullNamesModalOpen}
        setIsModalOpen={setIsAddingParticipantsByFullNamesModalOpen}
        setParticipantsChangingIsLoading={setParticipantsChangingIsLoading}
        setParticipants={setParticipants}
      />

      {!!checkingParticipantsEligibilityResult && (
        <CheckingParticipantsEligibilityResultModal
          eventId={eventId}
          isModalOpen={isCheckingParticipantsEligibilityResultModalOpen}
          setIsModalOpen={setIsCheckingParticipantsEligibilityResultModalOpen}
          checkingParticipantsResult={checkingParticipantsEligibilityResult}
          setParticipantsChangingIsLoading={setParticipantsChangingIsLoading}
          isInReportingContext={isInReportingContext}
          equippingLevel={equippingLevel}
          setParticipants={setParticipants}
        />
      )}
      {(materialsRequired || ageRequired) && (
        <FixEventModal
          eventId={eventId}
          mainFacilitatorId={mainFacilitatorId}
          eventType={eventType}
          isModalOpen={isFixEventModalOpen}
          materialsRequired={eventMaterialRequired}
          ageRequired={eventAgeRequired}
          setIsModalOpen={setIsFixEventModalOpen}
          setIsAddingParticipantsModalOpen={
            addingParticipantsType === AddingParticipantsTypes.emails
              ? setIsAddingParticipantsByEmailsModalOpen
              : setIsAddingParticipantsByFullNamesModalOpen
          }
          setMaterialRequired={setEventMaterialRequired}
          setAgeRequired={setEventAgeRequired}
        />
      )}
    </Flex>
  );
};

export default EventParticipants;
